import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { validationRequestLogsQueryKey } from '../validationRequestTimeline/ValidationRequestTimeline'

import { Validation } from '@percent/admin-dashboard/api/types'
import { OrganisationTypes } from '@percent/domain/verifications'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { AcknowledgeModal, ActionModal, Alert, Button, Flex, Modal, theme, Typography } from '@percent/lemonade'
import { useMutation, useQuery } from '@percent/utility'

type RequestDocumentationProps = {
  validation: Validation
  canRequest: boolean
  onRequested: () => void
}

// The rule pbp-api uses to pick who the email goes to: the applicant first, then the organisation.
const documentRequestRecipient = (submissionEmail?: string | null, validationRequestEmail?: string | null) =>
  [submissionEmail, validationRequestEmail].find(email => !!email?.trim()) ?? null

export function RequestDocumentation({ validation, canRequest, onRequested }: Readonly<RequestDocumentationProps>) {
  const { t } = useTranslation()
  const { adminService, validationSubmissionsService } = useServices()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [hasFailed, setHasFailed] = useState(false)

  const { data: logs } = useQuery(validationRequestLogsQueryKey(validation.id), adminService.getValidationRequestLogs, {
    id: validation.id
  })
  const { data: validationSubmission } = useQuery(
    ['/validation-submissions', validation.validationSubmissionId],
    validationSubmissionsService.getValidationSubmission,
    { id: validation.validationSubmissionId ?? '' },
    { enabled: !!validation.validationSubmissionId }
  )

  const [{ isPending, success }, { apiFunc: requestDocuments }] = useMutation(
    adminService.requestValidationRequestDocuments,
    () => {},
    () => setHasFailed(true)
  )

  const recipient = documentRequestRecipient(validationSubmission?.email, validation.email)
  const isRequested = !!logs?.some(log => log.type === 'document_requested')
  const isReceived = !!logs?.some(log => log.type === 'document_received')

  const handleClose = () => {
    if (isPending) return

    setIsModalOpen(false)

    if (success || hasFailed) {
      setHasFailed(false)
      onRequested()
    }
  }

  const renderModalContent = () => {
    if (success) {
      return (
        <AcknowledgeModal
          result="positive"
          title={t('dialog.requestValidationRequestDocumentation.success.title')}
          buttonText={t('button.close')}
          handleClose={handleClose}
          viewTestId="request-documentation-success"
          buttonTestId="request-documentation-success-close"
        />
      )
    }

    if (hasFailed) {
      return (
        <AcknowledgeModal
          result="negative"
          title={t('dialog.requestValidationRequestDocumentation.error.title')}
          description={t('dialog.requestValidationRequestDocumentation.error.description')}
          buttonText={t('button.close')}
          handleClose={handleClose}
          viewTestId="request-documentation-error"
          buttonTestId="request-documentation-error-close"
        />
      )
    }

    return (
      <ActionModal
        title={t('dialog.requestValidationRequestDocumentation.title')}
        primaryButtonText={t('dialog.requestValidationRequestDocumentation.title')}
        secondaryButtonText={t('button.cancel')}
        disabled={isPending}
        loading={isPending}
        handleClose={handleClose}
        handleSubmit={() => requestDocuments({ id: validation.id })}
        primaryBtnTestId="request-documentation-confirm"
        secondaryBtnTestId="request-documentation-cancel"
      >
        <Typography>
          {t('dialog.requestValidationRequestDocumentation.description', { email: recipient ?? '' })}
        </Typography>
      </ActionModal>
    )
  }

  const isRecipientKnown = !validation.validationSubmissionId || !!validationSubmission
  const showNotice = isRequested && !isReceived && !!recipient && isRecipientKnown
  const isNonprofit = !!validation.configuration?.allowedOrganisations?.types?.includes(OrganisationTypes.Nonprofit)
  const showButton = !isRequested && canRequest && !!recipient && isNonprofit && isRecipientKnown

  // Until the logs load we can't tell whether documents were already requested.
  if (!logs || (!showNotice && !showButton)) return null

  return (
    <Flex direction="column" gap={theme.spacing.m} w="100%">
      {showNotice && (
        <Alert variant="info" title={t('typography.validationRequestDocumentationRequested', { email: recipient })} />
      )}

      {showButton && (
        <Flex>
          <Button onClick={() => setIsModalOpen(true)} data-testid="request-documentation-button">
            {t('dialog.requestValidationRequestDocumentation.title')}
          </Button>
        </Flex>
      )}

      <Modal open={isModalOpen} onClose={handleClose} aria-labelledby="request-documentation-modal">
        {renderModalContent()}
      </Modal>
    </Flex>
  )
}
